import {Component, OnInit} from '@angular/core';
import {MatchDetailViewService} from '@modules/game-detail/services/match-detail-view.service';
import {GetAppDialogComponent} from '@shared/modules/shared-dialogs/dialogs/get-app-dialog/get-app-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {Sponsor} from '@shared/models/sponsor';
import {LinkService} from '@core/services/link.service';
import {LeagueType} from '@api/models/league/league-type';
import {AdCampaignService} from '@components/ad-campaign/services/ad-campaign.service';
import {AdCampaignPlacement} from '@api/models/ad-campaign/ad-campaign-placement';
import { LivestreamStatus } from '@api/models/livestream/streaming-info';
import {EntityType} from '@lib/models/entity/entity-type';

interface IMatchInfo {
  visible: boolean;
  text: string;
  subText?: string;
  title: string;
  link?: string;
}

@Component({
  selector: 'app-match-overview-tab',
  templateUrl: './match-overview-tab.component.html',
  styleUrls: ['./match-overview-tab.component.scss']
})
export class MatchOverviewTabComponent implements OnInit {

  public activeIndex: number = 0;
  public matchInfos: IMatchInfo[];
  public showLiveStreamBanner = false;
  public matchTitle: string;
  public startTimestamp: number;
  public entityType = EntityType.Event;

  constructor( public matchDetailViewService: MatchDetailViewService,
               public dialog: MatDialog,
               private adCampaignService: AdCampaignService ) { }

  public ngOnInit() {
    this.getMatchInfoData();
    this.adCampaignService.setPlacement(AdCampaignPlacement.Infos);
  }

  get fanModerationIsVisible(): boolean {
    if (this.matchDetailViewService.data && this.matchDetailViewService.data.match) {
      if (this.matchDetailViewService.data.match.fanModerationIsOpenForTicker()) {
        return true;
      }
    }
    return false;
  }

  get showMultipleFanModsInfo(): boolean {
    if (this.showTeamModRegisteredInfo) {
      return false;
    }
    return this.getRegisteredReporterString() != null;
  }

  get showTeamModRegisteredInfo(): boolean {
    return this.getRegisteredTeamReporterString() != null;
  }

  get loading(): boolean {
    if (!this.matchDetailViewService || !this.matchDetailViewService.data) {
      return true;
    }
    if (!this.matchDetailViewService.data.homeTeam || !this.matchDetailViewService.data.awayTeam) {
      return true;
    }
    return false;
  }

  public registerAsReporterClicked() {
    this.dialog.open(GetAppDialogComponent, GetAppDialogComponent.getDialogSize());
  }

  getReporterData(): any {
    const matchData = this.matchDetailViewService.data;
    const match = matchData.match;
    const teamName = matchData.currentTeam?.name;
    let text = `Klicke hier, um dich als Reporter für <b>${teamName}</b> anzumelden!`;
    if (this.showTeamModRegisteredInfo) {
      text = 'Du kannst dich für die Warteliste anmelden. Sollte der Ticker frei werden, wirst du benachrichtigt.';
    }
    const registerReporterData: any = {
      visible: false,
      text: text,
      image: '',
    };

    if (match.moderatorsOccupied() === false) {
      registerReporterData.visible = true;

      if (match.fanModerationIsImmediate()) {
        registerReporterData.image = 'assets/icons/add-bg-green.svg';
      } else {
        registerReporterData.image = 'assets/icons/add-bg-blue.svg';
      }
    }

    if (match.fanModeratorHome.moderatorIsFree() && match.fanModeratorAway.moderatorIsFree()) {
      let teamString = this.matchDetailViewService.data.homeTeam.name;
      teamString += ' oder ' + this.matchDetailViewService.data.awayTeam.name;
      registerReporterData.text = registerReporterData.text.replace('${team}', teamString);
    } else if (match.fanModeratorHome.moderatorIsFree()) {
      const teamString = this.matchDetailViewService.data.homeTeam.name;
      registerReporterData.text = registerReporterData.text.replace('${team}', teamString);
    } else if (match.fanModeratorHome.moderatorIsFree()) {
      const teamString = this.matchDetailViewService.data.awayTeam.name;
      registerReporterData.text = registerReporterData.text.replace('${team}', teamString);
    }

    return registerReporterData;
  }

  getRegisteredReporterString(): string {
    if (this.matchDetailViewService.data && this.matchDetailViewService.data.match) {
      const hasHomeFanReporter = this.matchDetailViewService.data.match.fanModeratorHome.atLeastOneFanModeratorRegistered();
      const hasAwayFanReporter = this.matchDetailViewService.data.match.fanModeratorAway.atLeastOneFanModeratorRegistered();

      if (hasHomeFanReporter || hasAwayFanReporter) {
        const teamString = this.getReportingTeamsString(hasHomeFanReporter, hasAwayFanReporter);
        return `Es haben sich bereits Interessenten für den Liveticker von ${teamString} angemeldet.`;
      }
    }
    return null;
  }

  getRegisteredTeamReporterString(): string {
    if (this.matchDetailViewService.data && this.matchDetailViewService.data.match) {
      const homeTeamHasTeamModerator = this.matchDetailViewService.data.match.teams.home.hasTeamModerator;
      const awayTeamHasTeamModerator = this.matchDetailViewService.data.match.teams.away.hasTeamModerator;

      if (homeTeamHasTeamModerator || awayTeamHasTeamModerator) {
        const teamString = this.getReportingTeamsString(homeTeamHasTeamModerator, awayTeamHasTeamModerator);
        return `Dieses Spiel wird von einem Teamreporter von ${teamString} getickert.`;
      }
    }

   return null;
  }

  getReportingTeamsString(homeModeratorPredicate: boolean, awayModeratorPredicate: boolean): string {
    if (homeModeratorPredicate && awayModeratorPredicate) {
      return this.matchDetailViewService.data.homeTeam.name + ' und ' + this.matchDetailViewService.data.awayTeam.name;
    } else if (homeModeratorPredicate) {
      return this.matchDetailViewService.data.homeTeam.name;
    } else if (awayModeratorPredicate) {
      return this.matchDetailViewService.data.awayTeam.name;
    }
  }

  private getMatchInfoData(): void {
    const match = this.matchDetailViewService.data.match;
    this.showLiveStreamBanner = match.streaming_info?.live_status === LivestreamStatus.Upcoming;
    this.startTimestamp = match.timestamps.start;
    this.matchTitle = match.teams.home.name + ' gegen ' + match.teams.away.name;
    const leagueName = match.league_info ? match.league_info.league_name : match.league_name;
    let matchLink = null;
    if (match.league_info.league_type !== LeagueType.Friendly) {
      matchLink = LinkService.getLeagueLink(match.league_info.league_readable_id);
    }

    let startDate = match.getDateFormatted(false, {
      weekday: 'long',
      year: '2-digit',
      month: '2-digit',
      day: '2-digit'
    });
    startDate = startDate + ' | ' + match.formattedTime;
    const referee = match.getRefereeName();
    this.matchInfos = [
      {
        title: 'Bewerb',
        visible: true,
        text: leagueName,
        subText: match.getRoundUIName(),
        link: matchLink,
      },
      {
        title: 'Anpfiff',
        visible: true,
        text: startDate
      },
      {
        title: 'Stadion',
        visible: !!match.stadium?.name,
        text: match.stadium?.name
      },
      {
        title: 'Schiedsrichter',
        visible: !!referee,
        text: referee
      },
      {
        title: 'Zuschauer',
        visible: !!match.spectators,
        text: match.spectators?.toString()
      },
    ];
  }

  public onPredictionGameClick() {
    this.dialog.open(GetAppDialogComponent, GetAppDialogComponent.getDialogSize());
  }

  public onSlideChange(event: any) {
    this.activeIndex = event;
  }

  public showPredictionGame(): boolean {
    if (!this.matchDetailViewService.data.match) {
      return true;
    }
    return this.matchDetailViewService.data.match.isPast() === false;
  }
}
